import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  StartAudio,
  useLocalParticipant,
  useRoomContext,
  useTracks,
  TrackLoop,
  ParticipantName,
  TrackMutedIndicator,
  ConnectionQualityIndicator,
  VideoTrack,
  useEnsureTrackRef,
  useIsMuted,
  ParticipantContextIfNeeded,
  TrackRefContextIfNeeded,
  useMaybeTrackRefContext,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track } from 'livekit-client';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2, ArrowLeft, MonitorUp, MonitorX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CALL_ROOM_OPTIONS } from '@/lib/livekit-room-options';
import { resolveApiUrl } from '@/providers/api-url';

const API_URL = resolveApiUrl();

function TileInner() {
  const trackRef = useEnsureTrackRef();
  const isVideoSource =
    trackRef.source === Track.Source.Camera || trackRef.source === Track.Source.ScreenShare;
  const hasPublication = trackRef.publication !== undefined;
  // Выключенная камера = publication есть, но muted. Без проверки isMuted
  // показывали бы чёрный/зависший кадр вместо плейсхолдера с именем.
  const isMuted = useIsMuted(trackRef);

  return (
    <div className="relative w-full h-full">
      {isVideoSource && hasPublication && !isMuted ? (
        <VideoTrack trackRef={trackRef} className="w-full h-full object-contain" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <span className="text-muted-foreground text-sm">
            <ParticipantName />
          </span>
        </div>
      )}
      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 text-xs text-white">
        <div className="flex items-center gap-1 px-2 py-1 rounded bg-black/50">
          <TrackMutedIndicator
            trackRef={{
              participant: trackRef.participant,
              source: Track.Source.Microphone,
            }}
          />
          <ParticipantName />
        </div>
        <ConnectionQualityIndicator className="px-1" />
      </div>
    </div>
  );
}

function ParticipantTile({ trackRef }: { trackRef?: unknown }) {
  const ctxTrackRef = useMaybeTrackRefContext();
  const effectiveTrackRef = (trackRef ?? ctxTrackRef) as {
    participant?: { identity: string };
    source?: Track.Source;
    publication?: unknown;
  } | undefined;

  return (
    <div className="relative rounded-xl overflow-hidden bg-muted border border-border w-full h-full min-h-0">
      <ParticipantContextIfNeeded
        {...(effectiveTrackRef?.participant ? { participant: effectiveTrackRef.participant as Parameters<typeof ParticipantContextIfNeeded>[0]['participant'] } : {})}
      >
        <TrackRefContextIfNeeded {...(effectiveTrackRef ? { trackRef: effectiveTrackRef as Parameters<typeof TrackRefContextIfNeeded>[0]['trackRef'] } : {})}>
          <TileInner />
        </TrackRefContextIfNeeded>
      </ParticipantContextIfNeeded>
    </div>
  );
}

function ParticipantGrid() {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  const cols = tracks.length <= 1 ? 1 : (tracks.length <= 4 ? 2 : 3);

  return (
    <div
      className="flex-1 grid gap-2 p-4 max-h-[calc(100vh-120px)]"
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridAutoRows: 'minmax(0, 1fr)',
      }}
    >
      <TrackLoop tracks={tracks}>
        <ParticipantTile />
      </TrackLoop>
    </div>
  );
}

function StageControls({ onLeave }: { onLeave: () => void }) {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } =
    useLocalParticipant();

  const toggleScreenShare = useCallback(() => {
    // audio: true — захватываем системный звук вкладки/экрана вместе с картинкой.
    // На отказ в выборе источника LiveKit бросает — глушим, чтобы не падал UI.
    localParticipant
      .setScreenShareEnabled(!isScreenShareEnabled, { audio: true })
      .catch(() => undefined);
  }, [localParticipant, isScreenShareEnabled]);

  return (
    <div className="flex items-center justify-center gap-3 p-4">
      <Button
        variant={isMicrophoneEnabled ? 'secondary' : 'destructive'}
        size="icon"
        className="rounded-full w-12 h-12"
        onClick={() => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled)}
      >
        {isMicrophoneEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
      </Button>
      <Button
        variant={isCameraEnabled ? 'secondary' : 'destructive'}
        size="icon"
        className="rounded-full w-12 h-12"
        onClick={() => localParticipant.setCameraEnabled(!isCameraEnabled)}
      >
        {isCameraEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
      </Button>
      <Button
        variant={isScreenShareEnabled ? 'default' : 'secondary'}
        size="icon"
        className="rounded-full w-12 h-12"
        title={isScreenShareEnabled ? 'Остановить демонстрацию экрана' : 'Демонстрация экрана'}
        onClick={toggleScreenShare}
      >
        {isScreenShareEnabled ? <MonitorX className="w-5 h-5" /> : <MonitorUp className="w-5 h-5" />}
      </Button>
      <Button
        variant="destructive"
        size="icon"
        className="rounded-full w-12 h-12"
        onClick={onLeave}
      >
        <PhoneOff className="w-5 h-5" />
      </Button>
    </div>
  );
}

function ConnectedStage({ onLeave }: { onLeave: () => void }) {
  const room = useRoomContext();
  const [connected, setConnected] = useState(room.state === 'connected');

  useEffect(() => {
    const onConnected = () => setConnected(true);
    const onDisconnected = () => onLeave();
    room.on('connected', onConnected);
    room.on('disconnected', onDisconnected);
    if (room.state === 'connected') setConnected(true);
    return () => {
      room.off('connected', onConnected);
      room.off('disconnected', onDisconnected);
    };
  }, [room, onLeave]);

  if (!connected) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <ParticipantGrid />
      <StageControls onLeave={() => { room.disconnect(); onLeave(); }} />
    </>
  );
}

export function StageRoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const [searchParams] = useSearchParams();
  // Переиспользуется и для STAGE-сцены, и для breakout-зала — подпись берётся
  // из ?label (название зала), по умолчанию «Сцена».
  const roomLabel = searchParams.get('label') ?? 'Сцена';
  const isSupport = roomLabel === 'Поддержка';
  const navigate = useNavigate();
  const [tokenData, setTokenData] = useState<{ token: string; serverUrl: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!roomId || fetchedRef.current) return;
    fetchedRef.current = true;

    async function fetchToken() {
      try {
        const response = await fetch(`${API_URL}/admin/calls/${roomId}/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const json = await response.json();
        const data = json.data ?? json;
        setTokenData({ token: data.token, serverUrl: data.serverUrl });
      } catch {
        setError('Не удалось получить токен для подключения');
      } finally {
        setLoading(false);
      }
    }

    fetchToken();
  }, [roomId]);

  const handleLeave = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      window.close();
    }
  }, [navigate]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !tokenData) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background">
        <p className="text-sm text-destructive">{error ?? 'Ошибка подключения'}</p>
        <Button variant="outline" onClick={handleLeave}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Назад
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border">
        <Button variant="ghost" size="sm" onClick={handleLeave}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Назад к трансляции
        </Button>
        {isSupport ? (
          <div className="flex items-center gap-2">
            <img
              src="/web-app-manifest-192x192.png"
              alt="Masters Club"
              className="h-7 w-7 rounded-full border border-border object-cover"
            />
            <span className="text-sm font-medium">{roomLabel}</span>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">{roomLabel}</span>
        )}
      </div>
      <LiveKitRoom
        token={tokenData.token}
        serverUrl={tokenData.serverUrl}
        audio
        video={false}
        connect
        options={CALL_ROOM_OPTIONS}
        className="flex-1 flex flex-col"
      >
        <RoomAudioRenderer />
        {/* Появляется только если браузер заблокировал автоплей звука —
            тап разблокирует прослушивание собеседника, кнопка сама прячется. */}
        <StartAudio
          label="Нажмите, чтобы включить звук"
          className="absolute left-1/2 top-16 z-20 -translate-x-1/2 rounded-full bg-primary px-4 py-2 text-sm text-white shadow-lg"
        />
        <ConnectedStage onLeave={handleLeave} />
      </LiveKitRoom>
    </div>
  );
}
